import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from '@/i18n'
import { Button } from './Button'
import { useUserChallenges } from '@/features/challenges/UserChallengeProvider'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { rescheduleAllNotifications } from '@/utils/simpleChallengeUtils'

export function NotificationSetupBanner() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { challengeRepository } = useUserChallenges()
  const [showBanner, setShowBanner] = useState(false)
  const [isScheduling, setIsScheduling] = useState(false)

  // Fetch challenges using the same pattern as HomePage
  const { data: challenges = [] } = useQuery({
    queryKey: ['challenges'],
    queryFn: async () => {
      return await challengeRepository.listByOwner(user?.uid)
    },
    enabled: !!user,
  })

  useEffect(() => {
    const checkNotificationStatus = async () => {
      // Check if user has active challenges
      const activeChallenges = challenges.filter(c => c.status === 'active')
      if (activeChallenges.length === 0) {
        setShowBanner(false)
        return
      }

      // Check notification permission status
      const permission = Notification.permission
      const notificationsEnabled = localStorage.getItem('notifications-enabled') !== 'false'
      const notificationsBannerDismissed = localStorage.getItem('notifications-banner-dismissed') === 'true'

      // If permissions are granted and notifications are enabled, automatically schedule them
      if (permission === 'granted' && notificationsEnabled) {
        // Check if we recently scheduled notifications
        const lastScheduled = localStorage.getItem('notifications-last-scheduled')
        const oneHourAgo = Date.now() - (60 * 60 * 1000)
        
        if (!lastScheduled || parseInt(lastScheduled) < oneHourAgo) {
          // Automatically schedule notifications without showing banner
          try {
            await rescheduleAllNotifications(activeChallenges)
            localStorage.setItem('notifications-last-scheduled', Date.now().toString())
            console.log(`✅ Automatically scheduled notifications for ${activeChallenges.length} active challenges`)
          } catch (error) {
            console.error('Failed to automatically schedule notifications:', error)
          }
        }
        setShowBanner(false)
        return
      }

      // Only show banner if permissions are not granted and we haven't dismissed it
      if (permission !== 'granted' && !notificationsBannerDismissed) {
        setShowBanner(true)
      } else {
        setShowBanner(false)
      }
    }

    checkNotificationStatus()
  }, [challenges])

  const handleRequestPermissions = async () => {
    try {
      setIsScheduling(true)
      
      // Request notification permissions
      const permission = await Notification.requestPermission()
      
      if (permission === 'granted') {
        // Enable notifications in localStorage
        localStorage.setItem('notifications-enabled', 'true')
        
        // Automatically schedule notifications for active challenges
        const activeChallenges = challenges.filter(c => c.status === 'active')
        await rescheduleAllNotifications(activeChallenges)
        localStorage.setItem('notifications-last-scheduled', Date.now().toString())
        
        console.log(`✅ Permissions granted and scheduled notifications for ${activeChallenges.length} active challenges`)
        setShowBanner(false)
      } else {
        // User denied permissions
        localStorage.setItem('notifications-enabled', 'false')
        localStorage.setItem('notifications-banner-dismissed', 'true')
        setShowBanner(false)
      }
    } catch (error) {
      console.error('Failed to request notification permissions:', error)
    } finally {
      setIsScheduling(false)
    }
  }

  const handleDismiss = () => {
    localStorage.setItem('notifications-banner-dismissed', 'true')
    setShowBanner(false)
  }

  if (!showBanner) return null

  return (
    <div className="bg-blue-50 dark:bg-blue-900/20 border-b border-blue-200 dark:border-blue-800">
      <div className="max-w-4xl mx-auto px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
            </div>
            <p className="text-sm text-blue-800 dark:text-blue-200">
              {t('notifications.permissionRequest')}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="primary"
              onClick={handleRequestPermissions}
              disabled={isScheduling}
            >
              {isScheduling ? t('common.loading') : t('notifications.enableNotifications')}
            </Button>
            <button
              onClick={handleDismiss}
              className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}